import { MutableRefObject, useCallback, useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { Box, Divider, Skeleton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../../../context/contextSelectors"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { FormCard } from "../../../../../../shared/components/forms/FormCard"
import { DashboardCardFullSizeSkeleton } from "../../../../../dashboards/components/DashboardCardSkeleton"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { useExportRateEntry } from "../../hooks/useExportRateEntry"
import { OccupancyRateEntry } from "../../statisticsTypes"

import { HeroStat, HeroStats } from "../resourceDashboard/HeroStats"
import { ResourceUsageBarList } from "../resourceDashboard/ResourceUsageBarList"
import { ResourceTypeSelectionFilter } from "../resourceDashboard/ResourceTypeSelectionFilter"
import {
    ResourceUsageSection,
    ResourceUsageSectionHandle,
    UsageColumn,
} from "../resourceDashboard/ResourceUsageSection"
import { UsageBar } from "../resourceDashboard/UsageBar"
import {
    ALL_RESOURCE_TYPES,
    DASHBOARD_RESOURCE_TYPES,
    ResourceTypeSelection,
    isResourceType,
    statusColor,
} from "../resourceDashboard/resourceDashboardTypes"
import { useOccupancyDashboardData } from "./useOccupancyDashboardData"

const OCCUPANCY_AXIS_MAX = 100

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: "1.875rem",
    },
    filtersCard: {
        padding: "1.5rem",
    },
    filtersRow: {
        display: "flex",
        gap: "1rem",
        alignItems: "flex-end",
        flexWrap: "wrap",
    },
    filtersSpacer: {
        flex: 1,
    },
    summaryInner: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        width: "100%",
    },
    sections: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
    },
    name: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    skeletonName: {
        width: "60%",
    },
    skeletonBar: {
        width: "100%",
        height: ".8125rem",
    },
    skeletonChip: {
        width: "4rem",
        height: "1.5rem",
    },
    chartTitle: {
        color: theme.palette.tailwind.slate[800],
    },
    emptyMessage: {
        color: theme.palette.tailwind.slate[500],
        textAlign: "center",
        padding: "2rem 0",
    },
}))

function averageOccupancyRate(entries: ReadonlyArray<OccupancyRateEntry>): number {
    if (entries.length === 0) return 0
    const total = entries.reduce((sum, entry) => sum + entry.occupancyRate, 0)
    return Math.round(total / entries.length)
}

export function Occupancy() {
    const { classes } = useStyles()
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{ startDate: DateTime; endDate: DateTime }>({ startDate, endDate })
    const [selection, setSelection] = useState<ResourceTypeSelection>(ALL_RESOURCE_TYPES)

    const { resultsByType, combinedEntries, presentTypes, summary, isInitialLoading } = useOccupancyDashboardData({
        serviceCenterId: serviceCenterId ?? 0,
        startDate: dateState.startDate.toISODate(),
        endDate: dateState.endDate.toISODate(),
    })

    const visibleTypes = useMemo<ReadonlyArray<ResourceType>>(
        () => (isResourceType(selection) ? [selection] : presentTypes),
        [selection, presentTypes],
    )
    const visibleTypeSet = useMemo(() => new Set(visibleTypes), [visibleTypes])
    const visibleEntries = useMemo(
        () => combinedEntries.filter((entry) => visibleTypeSet.has(entry.resourceType)),
        [combinedEntries, visibleTypeSet],
    )
    const sectionRefByType = useRef<Record<ResourceType, MutableRefObject<ResourceUsageSectionHandle | null>>>({
        [ResourceType.ROOM]: { current: null },
        [ResourceType.MOBILE_AV]: { current: null },
        [ResourceType.CAR]: { current: null },
    })

    const handleResourceClick = useCallback((resourceId: number, resourceType: ResourceType) => {
        sectionRefByType.current[resourceType].current?.scrollToResource(resourceId)
    }, [])

    const exportData = useMemo(() => visibleEntries.slice(), [visibleEntries])

    const exportOccupancies = useExportRateEntry({
        data: exportData,
        startDate: dateState.startDate,
        endDate: dateState.endDate,
        isOccupancyRate: true,
    })

    const columns = useMemo<ReadonlyArray<UsageColumn<OccupancyRateEntry>>>(
        () => [
            {
                id: "resource",
                headerId: "service-center-reports-statistic-occupancy-table-resource-title",
                headerFallback: "Resource",
                gridTrack: "minmax(8rem, 1.6fr)",
                renderCell: (entry) => (
                    <Typography variant="body1" className={classes.name}>
                        {entry.resource.displayName}
                    </Typography>
                ),
                renderSkeleton: () => <Skeleton variant="text" animation="wave" className={classes.skeletonName} />,
            },
            {
                id: "occupancy-rate",
                headerId: "service-center-reports-statistic-occupancy-table-occupancy-rate-title",
                headerFallback: "Occupancy rate",
                gridTrack: "minmax(10rem, 3fr)",
                renderCell: (entry) => (
                    <UsageBar
                        fillPct={entry.occupancyRate}
                        color={statusColor(entry.status.variant)}
                        valueLabel={`${entry.occupancyRate}%`}
                    />
                ),
                renderSkeleton: () => <Skeleton variant="rounded" animation="wave" className={classes.skeletonBar} />,
            },
            {
                id: "status",
                headerId: "service-center-reports-statistic-occupancy-table-status-title",
                headerFallback: "Status",
                gridTrack: "7rem",
                renderCell: (entry) => (
                    <StandardChip label={entry.status.label} variant={entry.status.variant} placement="table" />
                ),
                renderSkeleton: () => <Skeleton variant="rounded" animation="wave" className={classes.skeletonChip} />,
            },
        ],
        [classes],
    )

    const heroStats = useMemo<ReadonlyArray<HeroStat>>(() => {
        const overview = isResourceType(selection) ? undefined : summary
        return [
            {
                id: "occupancy-rate",
                value: `${overview?.overallOccupancyRate ?? averageOccupancyRate(visibleEntries)}%`,
                labelId: "service-center-reports-occupancy-dashboard-hero-overall-occupancy-label",
                labelFallback: "Overall occupancy",
            },
            {
                id: "resources-tracked",
                value: overview?.numberOfResources ?? visibleEntries.length,
                labelId: "service-center-reports-occupancy-dashboard-hero-resources-tracked-label",
                labelFallback: "Resources tracked",
            },
        ]
    }, [selection, summary, visibleEntries])

    const summaryContent = useMemo(() => {
        if (isInitialLoading) {
            return <DashboardCardFullSizeSkeleton />
        }

        const chart =
            visibleEntries.length > 0 ? (
                <ResourceUsageBarList
                    entries={visibleEntries}
                    getValue={(entry) => entry.occupancyRate}
                    axisMax={OCCUPANCY_AXIS_MAX}
                    getColor={(entry) => statusColor(entry.status.variant)}
                    formatValue={(value) => `${value}%`}
                    onResourceClick={handleResourceClick}
                />
            ) : (
                <LocalizedStrict id="service-center-reports-occupancy-dashboard-chart-empty">
                    <Typography variant="body2" className={classes.emptyMessage}>
                        No occupancy data for this period.
                    </Typography>
                </LocalizedStrict>
            )

        return (
            <Box className={classes.summaryInner}>
                <LocalizedStrict id="service-center-reports-occupancy-dashboard-chart-title">
                    <Typography variant="h6" className={classes.chartTitle}>
                        Occupancy by resource
                    </Typography>
                </LocalizedStrict>
                {chart}
                <Divider />
                <HeroStats stats={heroStats} />
            </Box>
        )
    }, [isInitialLoading, visibleEntries, heroStats, handleResourceClick, classes])

    const sectionsContent = useMemo(() => {
        if (!isInitialLoading && visibleTypes.length === 0) {
            return (
                <FormCard>
                    <LocalizedStrict id="service-center-reports-occupancy-dashboard-empty">
                        <Typography variant="body2" className={classes.emptyMessage}>
                            No resources found for this service center.
                        </Typography>
                    </LocalizedStrict>
                </FormCard>
            )
        }

        // On first load `presentTypes` isn't known yet, so render every type's section (each shows its
        // own in-table skeleton); once loaded we only keep the types that actually have resources.
        let typesToRender = visibleTypes
        if (isInitialLoading) {
            typesToRender = isResourceType(selection) ? [selection] : DASHBOARD_RESOURCE_TYPES
        }

        return typesToRender.map((resourceType) => (
            <ResourceUsageSection
                key={resourceType}
                resourceType={resourceType}
                queryResult={resultsByType[resourceType]}
                columns={columns}
                handleRef={sectionRefByType.current[resourceType]}
                emptyMessageId="service-center-reports-occupancy-dashboard-empty"
                emptyMessageFallback="No resources found for this service center."
            />
        ))
    }, [isInitialLoading, selection, visibleTypes, resultsByType, columns, classes])

    return (
        <Box className={classes.root}>
            <FormCard className={classes.filtersCard}>
                <Box className={classes.filtersRow}>
                    <LocalizedStrict
                        id="service-center-reports-statistic-occupancy-table-date-range-filter"
                        attrs={{ label: true }}
                    >
                        <StandardDateRangeFilter
                            value={dateState}
                            onChange={setDateState}
                            label="Date range"
                            variant="labeled"
                        />
                    </LocalizedStrict>
                    <ResourceTypeSelectionFilter value={selection} onChange={setSelection} />
                    <Box className={classes.filtersSpacer} />
                    <StandardExportButton onClick={exportOccupancies} />
                </Box>
            </FormCard>

            <FormCard>{summaryContent}</FormCard>

            <Box className={classes.sections}>{sectionsContent}</Box>
        </Box>
    )
}
